// src/components/Products/Financeiro/pages/DespesasPage.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Paper,
  TableContainer, MenuItem, Select, InputLabel, FormControl, Chip, Checkbox, Stack
} from '@mui/material';
import { Add, Edit, Delete, CalendarMonth } from '@mui/icons-material';
import {
  getDespesas,
  createDespesa,
  updateDespesa,
  deleteDespesa,
  getCategoriasDespesas,
  listDespesasRecorrentes,
} from '../services/api';
import {
  buildParcelaForMonth,
  computeDespesaStatus,
  formatDateInput,
  getRefDateForMonth,
  inferDespesaTipo,
  isProjectedId,
  parseMonthValue,
  sameMonthAs,
  toYyyyMm,
  clampDayToMonth, // <-- AJUSTE 1: Importado
} from './utils/despesas';

/* ======================= Form Vazio ======================= */

const emptyForm = {
  descricao: '',
  valor: '',
  data_vencimento: '',
  data_pagamento: '',
  categoria_id: '',
  observacoes: '',
};

/* ======================= Componente ======================= */

const DespesasPage = () => {
  const [categorias, setCategorias] = useState([]);
  const [despesasNaoRecorrentes, setDespesasNaoRecorrentes] = useState([]);
  const [recorrentesBase, setRecorrentesBase] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { ...item, editTargetId }
  const [form, setForm] = useState(emptyForm);
  const [selected, setSelected] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Filtro de mês
  const now = new Date();
  const defaultMonth = toYyyyMm(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)));
  const [selectedMonth, setSelectedMonth] = useState(defaultMonth);

  const categoriaMap = useMemo(
    () => new Map(categorias.map((c) => [c.id, c.nome])),
    [categorias]
  );

  /* ---------------- Carregamento ---------------- */

  const load = async () => {
    const [desp, recs, cats] = await Promise.all([
      getDespesas(),
      listDespesasRecorrentes(),
      getCategoriasDespesas(),
    ]);

    // AJUSTE 2: Função local 'inferTipo' removida daqui, pois não estava sendo usada.
    // A função 'inferDespesaTipo' (importada) já está sendo usada abaixo.

    setDespesasNaoRecorrentes(
      (Array.isArray(desp) ? desp : [])
        .filter((d) => !d.is_recorrente)
         // <-- AJUSTE APLICADO: Prioriza o 'd.tipo' do banco
        .map((d) => ({ ...d, tipo: d.tipo || inferDespesaTipo(d) }))
    );

    setRecorrentesBase(Array.isArray(recs) ? recs : []);
    setCategorias(cats || []);
  };

  useEffect(() => { load(); }, []);

  /* --------- Mês Selecionado + Data de Referência --------- */

  const parsedMonth = useMemo(() => parseMonthValue(selectedMonth), [selectedMonth]);

  const refDateForSelectedMonth = useMemo(() => {
    if (!parsedMonth) return new Date();
    const { year, monthIndex } = parsedMonth;
    return getRefDateForMonth(year, monthIndex);
  }, [parsedMonth]);

  /* ----------------- Listas por Mês ----------------- */

  // SOMENTE despesas avulsas aqui (nunca mostrar parceladas nesta tabela)
  const despesasDoMes = useMemo(() => {
    if (!parsedMonth) return [];
    const { year, monthIndex } = parsedMonth;

    return despesasNaoRecorrentes
      .filter((d) => d.tipo === 'avulsa')
      .filter((d) => sameMonthAs(d.data_pagamento || d.data_vencimento, year, monthIndex))
      .map((d) => ({
        ...d,
        isManageable: true,
        status: computeDespesaStatus({
          data_vencimento: d.data_vencimento,
          data_pagamento: d.data_pagamento,
          refDate: refDateForSelectedMonth,
        }),
      }));
  }, [despesasNaoRecorrentes, parsedMonth, refDateForSelectedMonth]);

  // Parceladas: PROJETAR a parcela específica do mês selecionado
  const despesasParceladas = useMemo(() => {
    if (!parsedMonth) return [];
    const { year, monthIndex } = parsedMonth;

    const bases = despesasNaoRecorrentes
      .filter((d) => d.tipo === 'parcelada' || d.data_termino); // heurística p/ identificar base

    const proj = bases
      .map((b) => {
        const parcela = buildParcelaForMonth(b, year, monthIndex, selectedMonth);
        return parcela ? { ...parcela, isManageable: false } : null;
      })
      .filter(Boolean);

    return proj;
  }, [despesasNaoRecorrentes, parsedMonth, selectedMonth]);

  // Projeção das recorrentes (editáveis no item base)
  const despesasRecorrentesProjetadas = useMemo(() => {
    if (!parsedMonth) return [];
    const { year, monthIndex } = parsedMonth;

    return recorrentesBase.map((r) => {
      const base = r?.data_vencimento ? new Date(r.data_vencimento) : null;
      const day = base && !Number.isNaN(base.getTime()) ? base.getUTCDate() : 1;
      const projected = clampDayToMonth(year, monthIndex, day).toISOString(); // <-- Agora funciona

      const proj = {
        ...r,
        id: `rec-${r.id}-${selectedMonth}`,
        baseId: r.id,
        isProjected: true,
        tipo: 'recorrente',
        data_vencimento: projected,
        data_pagamento: null,
      };

      return {
        ...proj,
        isManageable: false,
        status: computeDespesaStatus({
          data_vencimento: projected,
          data_pagamento: null,
          refDate: refDateForSelectedMonth,
        }),
      };
    });
  }, [recorrentesBase, parsedMonth, selectedMonth, refDateForSelectedMonth]);

  /* ----------------- Totais do Período ----------------- */

  const todasDespesas = useMemo(
    () => [...despesasDoMes, ...despesasRecorrentesProjetadas, ...despesasParceladas],
    [despesasDoMes, despesasRecorrentesProjetadas, despesasParceladas]
  );

  const totalPendente = useMemo(
    () =>
      todasDespesas
        .filter((d) => d.status === 'pendente')
        .reduce((acc, d) => acc + Number(d.valor || 0), 0),
    [todasDespesas]
  );

  const totalPago = useMemo(
    () =>
      todasDespesas
        .filter((d) => d.status === 'paga')
        .reduce((acc, d) => acc + Number(d.valor || 0), 0),
    [todasDespesas]
  );

  const itemById = useMemo(() => {
    const m = new Map();
    todasDespesas.forEach((it) => m.set(it.id, it));
    return m;
  }, [todasDespesas]);

  /* ----------------- Seleção consistente ----------------- */

  useEffect(() => {
    setSelected((prev) => {
      const validIds = new Set(todasDespesas.filter((d) => d.isManageable !== false).map((d) => d.id));
      const next = new Set([...prev].filter((id) => validIds.has(id)));
      return next;
    });
  }, [todasDespesas]);

  /* ----------------- CRUD ----------------- */

  const handleOpen = (despesa = null) => {
    if (despesa && despesa.isManageable === false) return;

    if (despesa) {
      const editTargetId = isProjectedId(despesa.id) ? (despesa.baseId ?? null) : despesa.id;
      setEditing({ ...despesa, editTargetId });

      setForm({
        descricao: despesa.descricao || '',
        valor: despesa.valor || '',
        data_vencimento: (() => {
          const raw = despesa.isProjected ? despesa.data_vencimento : despesa.data_vencimento;
          if (!raw) return '';
          const date = new Date(raw);
          return Number.isNaN(date.getTime()) ? '' : formatDateInput(date);
        })(),
        data_pagamento: despesa.data_pagamento ? (() => {
          const date = new Date(despesa.data_pagamento);
          return Number.isNaN(date.getTime()) ? '' : formatDateInput(date);
        })() : '',
        categoria_id: despesa.categoria_id || '',
        observacoes: despesa.observacoes || '',
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  const handleSave = async () => {
    const { categoria_id, observacoes, data_pagamento, data_vencimento, ...rest } = form;

    const payload = {
      ...rest,
      data_vencimento: data_vencimento || null,
      data_pagamento: data_pagamento || null,
      categoria_id: categoria_id || null,
      observacoes: observacoes || '',
      is_recorrente: false,
      recorrencia_tipo: null,
      recorrencia_fim: null,
      tipo: null,
      status: computeDespesaStatus({
        data_vencimento: data_vencimento,
        data_pagamento: data_pagamento,
        refDate: refDateForSelectedMonth,
      }),
    };

    try {
      if (editing) await updateDespesa(editing.id, payload);
      else await createDespesa(payload);

      handleClose();
      load();
    } catch (e) {
      alert('Não foi possível salvar a despesa.');
    }
  };

  const resolveDeleteTargetId = (id) => {
    if (!isProjectedId(id)) return id;
    const item = itemById.get(id);
    return item?.baseId ?? null;
  };

  const handleDelete = async (id) => {
    const targetId = resolveDeleteTargetId(id);
    if (!targetId) return;
    const item = itemById.get(id);
    if (item?.isManageable === false) return;

    if (!window.confirm('Excluir despesa? Esta ação não pode ser desfeita.')) return;
    try {
      await deleteDespesa(targetId);
      load();
    } catch (e) {
      alert('Não foi possível excluir a despesa.');
    }
  };

  const handleDeleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Excluir ${selected.size} despesas selecionadas?`)) return;
    setIsBulkDeleting(true);
    try {
      const ids = [...selected].map((id) => resolveDeleteTargetId(id)).filter(Boolean);
      const uniqueIds = [...new Set(ids)];
      await Promise.all(uniqueIds.map((id) => deleteDespesa(id)));
      setSelected(new Set());
      load();
    } catch {
      alert('Não foi possível excluir as despesas selecionadas.');
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const toggleSelect = (id) => {
    const item = itemById.get(id);
    if (!item || item.isManageable === false) return;

    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleToggleSelectGroup = (items) => {
    const manageable = items.filter((i) => i.isManageable !== false);
    const allSelected = manageable.length > 0 && manageable.every((i) => selected.has(i.id));
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) manageable.forEach((i) => next.delete(i.id));
      else manageable.forEach((i) => next.add(i.id));
      return next;
    });
  };

  /* ----------------- Render Tabela ----------------- */

  const renderTable = (items, titulo, { showDataTermino = false } = {}) => {
    const manageableItems = items.filter((i) => i.isManageable !== false);
    const allSelected = manageableItems.length > 0 && manageableItems.every((i) => selected.has(i.id));
    const someSelected = manageableItems.some((i) => selected.has(i.id));
    const emptyColSpan = showDataTermino ? 8 : 7;

    return (
      <Paper sx={{ p: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6" fontWeight={700}>{titulo}</Typography>
          <Typography variant="body2" color="text.secondary">
            {items.length} registro{items.length === 1 ? '' : 's'}
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={!allSelected && someSelected}
                    onChange={() => handleToggleSelectGroup(items)}
                    disabled={manageableItems.length === 0}
                  />
                </TableCell>
                <TableCell>Descrição</TableCell>
                <TableCell>Valor</TableCell>
                <TableCell>Categoria</TableCell>
                <TableCell>Vencimento</TableCell>
                {showDataTermino && <TableCell>Término</TableCell>}
                <TableCell>Status</TableCell>
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((d) => (
                <TableRow key={d.id} hover sx={{ opacity: d.status === 'paga' ? 0.6 : 1 }}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={d.isManageable !== false && selected.has(d.id)}
                      onChange={() => toggleSelect(d.id)}
                      color="primary"
                      disabled={d.isManageable === false}
                    />
                  </TableCell>
                  <TableCell>{d.descricao}</TableCell>
                  <TableCell>{Number(d.valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</TableCell>
                  <TableCell>
                    {d.categoria_id ? <Chip label={categoriaMap.get(d.categoria_id) || '...'} size="small" /> : '-'}
                  </TableCell>
                  <TableCell>
                    {d.data_vencimento ? new Date(d.data_vencimento).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                  </TableCell>
                  {showDataTermino && (
                    <TableCell>
                      {d.data_termino ? new Date(d.data_termino).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}
                    </TableCell>
                  )}
                  <TableCell>{d.status}</TableCell>
                  <TableCell align="right">
                    {d.isManageable === false ? (
                      <Typography variant="body2" color="text.secondary">Gerencie pelo cadastro</Typography>
                    ) : (
                      <>
                        <IconButton color="primary" onClick={() => handleOpen(d)} aria-label="Editar despesa"><Edit /></IconButton>
                        <IconButton color="error" onClick={() => handleDelete(d.id)} aria-label="Excluir despesa"><Delete /></IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={emptyColSpan} align="center" sx={{ py: 4 }}>
                    Nenhuma despesa cadastrada.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  /* ----------------- UI ----------------- */

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2 }}>
        <Typography variant="h5" fontWeight={700}>Despesas Mensais</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <CalendarMonth fontSize="small" />
          <TextField
            type="month"
            size="small"
            label="Mês"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={handleDeleteSelected}
            disabled={selected.size === 0 || isBulkDeleting}
          >
            {isBulkDeleting ? 'Excluindo...' : 'Excluir Selecionadas'}
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen()}>Adicionar Despesa Avulsa</Button>
        </Stack>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 2, mb: 2 }}>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">Total Pendente (Mês)</Typography>
          <Typography variant="h5" fontWeight={700} color="error.main">
            {totalPendente.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </Typography>
        </Paper>
        <Paper sx={{ p: 2, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">Total Pago (Mês)</Typography>
          <Typography variant="h5" fontWeight={700} color="success.main">
            {totalPago.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </Typography>
        </Paper>
      </Box>

      <Box sx={{ display: 'grid', gap: 2 }}>
        {renderTable(despesasDoMes, 'Despesas do Mês')}
        {renderTable(despesasRecorrentesProjetadas, 'Despesas Recorrentes')}
        {renderTable(despesasParceladas, 'Despesas Parceladas', { showDataTermino: true })}
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>{editing ? 'Editar Despesa Avulsa' : 'Nova Despesa Avulsa'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <TextField label="Descrição" name="descricao" value={form.descricao} onChange={handleChange} fullWidth />
          <TextField label="Valor" name="valor" type="number" value={form.valor} onChange={handleChange} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Categoria</InputLabel>
            <Select name="categoria_id" label="Categoria" value={form.categoria_id} onChange={handleChange}>
              {categorias.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.nome}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Data de Vencimento"
            name="data_vencimento"
            type="date"
            value={form.data_vencimento}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Data de Pagamento"
            name="data_pagamento"
            type="date"
            value={form.data_pagamento}
            onChange={handleChange}
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <TextField label="Observações (Opcional)" name="observacoes" value={form.observacoes} onChange={handleChange} fullWidth multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Salvar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DespesasPage;